import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import SiteFooter from "./components/footer/SiteFooter";
import CTABanner from "./components/CTABanner";
import { useSmoothScroll } from "./lib/useSmoothScroll";
import Home from "./pages/Home";
import Work from "./pages/work/Work";
import CaseStudy from "./pages/work/CaseStudy";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Login from "./pages/Login";
import Questionnaire from "./pages/Questionnaire";
import Sitemap from "./pages/Sitemap";
import NotFound from "./pages/NotFound";
import {
  TermsOfService,
  RefundPolicy,
  PrivacyPolicy,
  CookiePolicy,
  YourData,
} from "./pages/Legal";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminOverview from "./pages/admin/OverviewSection";
import AdminRequests from "./pages/admin/RequestsSection";
import AdminProjects from "./pages/admin/ProjectsSection";
import AdminLeads from "./pages/admin/LeadsSection";
import AccountsSection from "./pages/admin/AccountsSection";
import UsersSection from "./pages/admin/UsersSection";
import AdminMessages from "./pages/admin/MessagesSection";
import NotificationsSection from "./pages/admin/NotificationsSection";
import AdminInvoices from "./pages/admin/InvoicesSection";
import BlogSection from "./pages/admin/BlogSection";
import CaseStudiesSection from "./pages/admin/CaseStudiesSection";
import LayoutSection from "./pages/admin/LayoutSection";
import ProductsSection from "./pages/admin/ProductsSection";
import UsageSection from "./pages/admin/UsageSection";
import AdminProjectTasks from "./pages/admin/ProjectTasks";
import ClientProjectTasks from "./pages/client/ProjectTasks";
import ClientLayout from "./pages/client/ClientLayout";
import ClientHome from "./pages/client/HomeSection";
import ClientProjects from "./pages/client/ProjectsSection";
import ClientMessages from "./pages/client/MessagesSection";
import ClientTeam from "./pages/client/TeamSection";
import ClientInvoices from "./pages/client/InvoicesSection";
import { AuthProvider } from "./auth/AuthContext";
import ProtectedRoute from "./auth/ProtectedRoute";
import "./App.css";

const App = () => {
  const { pathname } = useLocation();
  useSmoothScroll(); // site-wide smooth scroll (skips /admin & /dashboard)

  // The footer renders on every route. The CTA only closes out content pages
  // that would otherwise dead-end — Home, Work and Case Study already carry
  // their own bespoke closers, and the intake / login / logged-in app shells
  // shouldn't show a "start a project" pitch.
  const showCTA =
    pathname.startsWith("/blog") || pathname.startsWith("/legal");

  return (
    <AuthProvider>
      <div className="App">
        {/* No app-level <Helmet>: every route below renders its own <Seo>,
            which owns title, description, canonical, OG/Twitter and robots.
            A shared one here would only fight the page-level tags. */}
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/work" element={<Work />} />
          <Route path="/work/:id" element={<CaseStudy />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/login" element={<Login />} />
          <Route path="/start" element={<Questionnaire />} />
          <Route path="/sitemap" element={<Sitemap />} />
          <Route path="/legal/terms" element={<TermsOfService />} />
          <Route path="/legal/refunds" element={<RefundPolicy />} />
          <Route path="/legal/privacy" element={<PrivacyPolicy />} />
          <Route path="/legal/cookies" element={<CookiePolicy />} />
          <Route path="/legal/your-data" element={<YourData />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute require="admin">
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminOverview />} />
            <Route path="requests" element={<AdminRequests />} />
            <Route path="projects" element={<AdminProjects />} />
            <Route
              path="projects/:projectId/tasks"
              element={<AdminProjectTasks />}
            />
            <Route path="leads" element={<AdminLeads />} />
            <Route path="accounts" element={<AccountsSection />} />
            <Route path="case-studies" element={<CaseStudiesSection />} />
            <Route path="layout" element={<LayoutSection />} />
            <Route path="products" element={<ProductsSection />} />
            <Route path="usage" element={<UsageSection />} />
            <Route path="users" element={<UsersSection />} />
            <Route path="clients" element={<Navigate to="/admin/users" replace />} />
            <Route path="blog" element={<BlogSection />} />
            <Route path="messages" element={<AdminMessages />} />
            <Route path="notifications" element={<NotificationsSection />} />
            <Route path="invoices" element={<AdminInvoices />} />
          </Route>
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute require="client">
                <ClientLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<ClientHome />} />
            <Route path="projects" element={<ClientProjects />} />
            <Route
              path="projects/:projectId/tasks"
              element={<ClientProjectTasks />}
            />
            <Route path="messages" element={<ClientMessages />} />
            <Route path="team" element={<ClientTeam />} />
            <Route path="invoices" element={<ClientInvoices />} />
          </Route>

          {/* Catch-all. Netlify's SPA fallback answers every unknown URL with
              a 200, so this is what stops a typo'd link from being indexed as
              a valid, empty page. */}
          <Route path="*" element={<NotFound />} />
        </Routes>

        {showCTA && <CTABanner />}
        <SiteFooter />
      </div>
    </AuthProvider>
  );
};

export default App;
